import {
  Box,
  TextField,
  Typography,
  InputLabel,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  TableRow,
  TableCell,
  Chip,
} from "@mui/material";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import PageTitle from "../PageTitle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useContext, useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import toast from "react-hot-toast";
import SaveIcon from "@mui/icons-material/Save";
import FileStructure from "./FileStructure";
import axios from "axios";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import CodeIcon from "@mui/icons-material/Code";
import EnhancedTable from "../../layouts/table";
import LoadingTableRows from "../LoadingTableRows";
import DeleteIcon from "@mui/icons-material/Delete";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { getShortenAddress } from "../../scripts/constants";
import PreviewIcon from "@mui/icons-material/Preview";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import NewDeploymentModal from "./NewDeploymentModal";
import DeployDetailsModal from "./DeployDetailsModal";
import { AppContext } from "../../scripts/context/App";
import ConfirmationModal from "../ConfirmationModal";

export const downloadJsonfile = async (project_uuid: string, type: string) => {
  try {
    const response = await axios.get("/deploy/download-json", {
      params: {
        project_uuid,
        type,
      },
    });

    const string = JSON.stringify(response.data, null, 2);
    const blob = new Blob([string], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${type}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error: any) {
    console.log(error);

    toast.error(
      error.response.data.message ||
      "Error downloading file. Please check your console for more details"
    );
  }
};

export default function EditSmartContract() {
  let [navigate, { uuid }] = [useNavigate(), useParams()];
  const { user } = useContext(AppContext);
  const {
    project: projectData,
    getProjectDetails,
    setState,
  }: any = useOutletContext();
  const [newDeployment, setNewDeployment] = useState<any>({
    status: false,
    data: {
      values: {},
    },
  });
  const [detailsModal, setDetailsModal] = useState<any>({
    status: false,
    loading: false,
    data: {},
  });
  const [newDeploymentLoading, setNewDeploymentLoading] = useState(false);
  const [paginationData, setPaginationDatas] = useState<any>({
    page: 1,
    pagesCount: -1,
    rowsPerPage: 10,
    rowsPerPageOptions: [2, 5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Deployment Name",
      },
      {
        id: "uuid",
        numeric: false,
        disablePadding: false,
        label: "Deployment UUID",
      },
      {
        id: "smart_contract_address",
        numeric: false,
        disablePadding: false,
        label: "Smart Contract Address",
      },
      {
        id: "network_chain",
        numeric: false,
        disablePadding: false,
        label: "Chain Network",
      },
      {
        id: "deployment_status",
        numeric: false,
        disablePadding: false,
        label: "Deployment Status",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: "Actions",
      },
    ],
    tableItems: [],
  });
  const setPaginationData = (object: any) =>
    setPaginationDatas((prevState: any) => ({ ...prevState, ...object }));
  const [order, setOrder] = useState<any>({
    order: "asc",
    orderBy: "name",
  });
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState({
    description: projectData.description,
    name: projectData.name,
  });
  const [confirmDelete, setConfirmDelete] = useState({
    status: false,
    id: "",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setInputs({ ...inputs, [name]: value });
  };

  const updateProject = async () => {
    if (!user.permissions.includes("update_project")) return toast.error("You don't have permission to do this");
    if (!inputs.name.trim() || !inputs.description.trim()) return toast.error("Please fill all the fields");

    try {
      await axios.post("/smart-contract/update-project", {
        uuid,
        name: inputs.name,
        description: inputs.description,
      });

      toast.success("Project updated successfully");
      setState({
        project: {
          ...projectData,
          name: inputs.name,
          description: inputs.description,
        },
      });
    } catch (error: any) {
      if (error.response.data.errors) {
        toast.error(
          error.response.data.errors.description[0] || error.response.data.errors.name[0] || "Error updating project. Please check your console for more details"
        )
      } else {
        toast.error(error.response.data.message || "Error updating project");
      }

      console.log({ error });
    }
  };

  useEffect(() => {
    setInputs({
      description: projectData.description,
      name: projectData.name,
    });
  }, [projectData.description, projectData.name]);

  useEffect(() => {
    if (!uuid || !user.permissions.includes("view_project_details")) return;
    getProjectDetails(uuid);
  }, []);

  const getDeployments = async (params?: any) => {
    if (!user.permissions.includes("view_deployment")) return;

    if (!params) {
      // it's means that it's the first time that the component is loaded
      setLoading(true);
    }

    try {
      const {
        data: { data },
      } = await axios.get("/deploy/get-deployments", {
        params: {
          project_uuid: uuid,
          ...params,
        },
      });

      setPaginationData({
        tableItems: data.data,
        pagesCount: data.total,
        rowsPerPage: Number(data.per_page),
        page: data.current_page,
      });
    } catch (error: any) {
      console.log({ error });

      toast.error(
        error.response.data.message ||
        "Error while getting deployments. Please check your console for more details"
      );
    }
    setLoading(false);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    getDeployments({
      ...(order.orderBy
        ? {
          sortBy: order.orderBy,
          sortOrder: order.order,
        }
        : {}),
      page: 1,
      perPage: event.target.value,
    });
  };
  const handleChangePage = (_: unknown, newPage: number) => {
    getDeployments({
      ...(order.orderBy
        ? {
          sortBy: order.orderBy,
          sortOrder: order.order,
        }
        : {}),
      page: newPage,
      perPage: paginationData.rowsPerPage,
    });
  };

  const deleteDeploy = async (uuid: string | number) => {
    if (!user.permissions.includes("delete_deployment")) return toast.error("You don't have permission to do this");

    try {
      await axios.delete("/deploy/delete", {
        params: {
          uuid,
        },
      });

      setConfirmDelete({ status: false, id: "" });
      toast.success("Deployment deleted successfully");
      setPaginationData({
        tableItems: paginationData.tableItems.filter(
          (item: any) => item.uuid !== uuid
        ),
      })
    } catch (error: any) {
      setConfirmDelete({ status: false, id: "" });
      toast.error(
        error.response.data.message || "Error while deleting deployment"
      );

      console.log({ error });
    }
  };

  const deployCompiledFile = async () => {
    if (!user.permissions.includes("create_deployment")) return toast.error(
      "You don't have permission to deploy this project"
    );

    try {
      setNewDeploymentLoading(true);
      const [
        {
          data: { data },
        },
        { data: bytecode },
        { data: abi },
      ] = await Promise.all([
        await axios.get("/smart-contract/get-project-deployment-data", {
          params: {
            project_uuid: uuid,
          },
        }),
        axios.get("/deploy/download-json", {
          params: { project_uuid: uuid, type: "bytecode" },
        }),
        axios.get("/deploy/download-json", {
          params: { project_uuid: uuid, type: "abi" },
        }),
      ]);

      setNewDeployment({
        status: true,
        data: {
          ...data,
          bytecode,
          abi,
        },
      });
    } catch (error: any) {
      console.log({ error });

      toast.error(
        error.response.data.message ||
        "Error while get project deployment data. Please check your console for more details"
      );
    }
    setNewDeploymentLoading(false);
  };

  const preview = async (deployUUID: string) => {
    setDetailsModal({
      ...detailsModal,
      loading: deployUUID,
    });

    try {
      const {
        data: { data },
      } = await axios.get("/deploy/details", {
        params: {
          uuid: deployUUID,
        },
      });

      setDetailsModal({
        ...detailsModal,
        status: true,
        loading: false,
        data,
      });
    } catch (error: any) {
      console.log(error);

      toast.error(
        error.response.data.message ||
        "Something went wrong. Please check your console for more details"
      );
      setDetailsModal({
        ...detailsModal,
        loading: false,
      });
    }
  };

  if (!user.permissions.includes("view_project_details")) return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <h3>You dont have permissions to view this page</h3>
    </Box>
  )
  return (
    <>
      <PageTitle
        title="Smart Contract Management"
        description={`Edit ${projectData.name} Smart Contract Project`}
        actionLabel="Go back"
        action={() => {
          navigate("/dashboard/smart-contract-management");
        }}
        startIcon={<ArrowBackIcon />}
      />

      <NewDeploymentModal
        data={newDeployment}
        setData={setNewDeployment}
        updateTableItems={(newObject: any) => {
          setPaginationData({
            tableItems: [newObject, ...paginationData.tableItems],
          });
        }}
        handleClose={() => setNewDeployment({ status: false, data: null })}
      />

      <DeployDetailsModal
        data={detailsModal}
        handleClose={() => {
          setDetailsModal({
            ...detailsModal,
            status: false,
          });
        }}
      />

      <ConfirmationModal
        open={confirmDelete.status}
        onClose={() => setConfirmDelete({ ...confirmDelete, status: false })}
        onConfirm={() => deleteDeploy(confirmDelete.id)}
        title="Are you sure?"
        message={
          "This action cannot be undone, please confirm to continue"
        }
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: "1 1 300px",
            }}
          >
            <TextField
              disabled={!user.permissions.includes("update_project")}
              id="name"
              name="name"
              label="Project Name"
              variant="standard"
              value={inputs.name}
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
            />
            <Box>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Project UUID</InputLabel>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">{uuid}</Typography>
                <Box>
                  <Tooltip title="Copy UUID">
                    <IconButton
                      onClick={() =>
                        navigator.clipboard.writeText(uuid || "").then(() => {
                          toast.success("Copied to clipboard");
                        })
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download ABI">
                    <IconButton onClick={() => {
                      if (!user.permissions.includes("download_json")) return toast.error("You don't have permission to download abi");

                      downloadJsonfile(uuid as string, "abi");
                    }}>
                      <FormatColorTextIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Bytecode">
                    <IconButton onClick={() => {
                      if (!user.permissions.includes("download_json")) return toast.error("You don't have permission to download bytecode");

                      downloadJsonfile(uuid as string, "bytecode");
                    }}>
                      <CodeIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: "1 1 300px",
            }}
          >
            <Box>
              <TextField
                disabled={!user.permissions.includes("update_project")}
                id="description"
                name="description"
                label="Description"
                variant="standard"
                value={inputs.description || ""}
                onChange={handleChange}
                fullWidth
                multiline
                inputProps={{ maxLength: 2000 }}
                maxRows={4}
              />
            </Box>
            <Box
              sx={{
                flex: "1",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
              }}
            >
              <InputLabel sx={{ fontSize: "0.75rem" }}>
                Compile Status
              </InputLabel>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">{projectData.status}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            if (!user.permissions.includes("update_project")) return toast.error("You don't have permission to do this");

            updateProject();
          }}
          startIcon={<SaveIcon />}
          sx={{ mt: 3 }}
        >
          Save
        </Button>

        <FileStructure />

        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={
                newDeploymentLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <RocketLaunchIcon />
                )
              }
              onClick={() => {
                if (!user.permissions.includes("create_deployment")) return toast.error(
                  "You don't have permission to deploy this project"
                );
                deployCompiledFile();
              }}
              disabled={newDeploymentLoading}
            >
              New Deploy
            </Button>
          </Box>
          <EnhancedTable
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            TableRows={
              loading ? (
                <LoadingTableRows
                  elementsCount={paginationData.tableHead.length}
                />
              ) : paginationData.tableItems.length > 0 ? (
                paginationData.tableItems.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.uuid}</TableCell>
                    <TableCell>
                      {getShortenAddress(row.smart_contract_address)}
                    </TableCell>
                    <TableCell>{row.network_chain}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        variant="outlined"
                        color={
                          row.status === "Deployed" ? "primary" : "secondary"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title="Copy UUID"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(row.uuid || "")
                            .then(() => {
                              toast.success("Copied to clipboard");
                            });
                        }}
                      >
                        <IconButton color="primary">
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy smart-contract Address">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(row.smart_contract_address || "")
                              .then(() => {
                                toast.success("Copied to clipboard");
                              });
                          }}
                        >
                          <BookOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Show Details">
                        <IconButton
                          color="primary"
                          disabled={detailsModal.loading === row.uuid}
                          onClick={() => preview(row.uuid)}
                        >
                          <PreviewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => {
                            if (!user.permissions.includes("delete_deployment")) return toast.error("You don't have permission to delete deployments");

                            setConfirmDelete({
                              status: true,
                              id: row.uuid
                            })
                            // deleteDeploy(row.uuid);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                user.permissions.includes("view_deployment") ? <TableRow>
                  <TableCell colSpan={paginationData.tableHead.length}>
                    <Typography
                      sx={{ textAlign: "center" }}
                      variant="body2"
                      color="textPrimary"
                    >
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow> : (
                  <TableRow>
                    <TableCell colSpan={paginationData.tableHead.length}>
                      <Typography
                        sx={{ textAlign: "center" }}
                        variant="body2"
                        color="textPrimary"
                      >
                        You don't have permission to view this data
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              )
            }
            handleRequestSortHeader={(orderFunc, orderByFunc) => {
              if (!orderFunc) return;

              getDeployments({
                sortBy: orderByFunc,
                sortOrder: orderFunc,
                page: 1,
                perPage: paginationData.rowsPerPage,
              });

              setOrder({
                order: orderFunc,
                orderBy: orderByFunc,
              });
            }}
            {...paginationData}
          >
            <></>
          </EnhancedTable>
        </Box>
      </Box>
    </>
  );
}
